import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { DEFAULT_CURRENCY, majorToMinor, minorToMajor } from "@/core/utils/func/money-minor";
import { useAddPayrollLine } from "@/features/payroll/api/use-add-payroll-line";
import { useUpdatePayrollLine } from "@/features/payroll/api/use-update-payroll-line";
import type {
  PayrollBatchLineDto,
  PayrollBatchLineInputDto,
} from "@/features/payroll/api/dto";
import {
  createPayrollBatchLineSchema,
  type PayrollBatchLineFormValues,
} from "@/features/payroll/schemas/payroll-batch.schema";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: string;
  /** Si fourni, mode édition : préremplit et appelle PUT lines/{lineId}. */
  line?: PayrollBatchLineDto;
}

/**
 * Ajoute (ou édite) une ligne (salarié) dans une paie `DRAFT` / `SIMULATED`.
 * La paie revient en `DRAFT` après succès (invalidation query). Preview
 * `MutationConfirmPreview` systématique.
 */
export function PayrollLineFormDialog({ open, onOpenChange, batchId, line }: Props) {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const tPayroll = (key: string) => t(key as TFunctionType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schema = useMemo(() => createPayrollBatchLineSchema(), [i18n.language]);

  const addLine = useAddPayrollLine(batchId);
  const updateLine = useUpdatePayrollLine(batchId);
  const pending = addLine.isPending || updateLine.isPending;
  const [preview, setPreview] = useState<PayrollBatchLineInputDto | null>(null);

  const defaults: PayrollBatchLineFormValues = line
    ? {
        employeeName: line.employeeName,
        employeeReference: line.employeeReference ?? "",
        phone: line.phone,
        amount: minorToMajor(line.amountMinor, DEFAULT_CURRENCY),
      }
    : {
        employeeName: "",
        employeeReference: "",
        phone: "",
        amount: 0,
      };

  const form = useForm({
    defaultValues: defaults,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview({
        employeeName: parsed.data.employeeName,
        employeeReference: parsed.data.employeeReference || null,
        phone: parsed.data.phone,
        amountMinor: majorToMinor(parsed.data.amount, DEFAULT_CURRENCY),
      });
    },
  });

  const isEdit = Boolean(line);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              {isEdit
                ? t("payroll.lines.edit.title")
                : t("payroll.lines.add.title")}
            </DialogTitle>
          </DialogHeader>

          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="employeeName">
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="line-name">
                    {t("payroll.lines.form.employeeName")}
                  </Label>
                  <Input
                    id="line-name"
                    maxLength={120}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="employeeReference">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="line-ref">
                    {t("payroll.lines.form.employeeReference")}
                  </Label>
                  <Input
                    id="line-ref"
                    maxLength={80}
                    placeholder={t(
                      "payroll.lines.form.employeeReferencePlaceholder"
                    )}
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="phone">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="line-phone">
                    {t("payroll.lines.form.phone")}
                  </Label>
                  <Input
                    id="line-phone"
                    className="notranslate"
                    placeholder="+22462…"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="amount">
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="line-amount">
                    {t("payroll.lines.form.amount")}
                  </Label>
                  <Input
                    id="line-amount"
                    type="number"
                    min={1}
                    className="notranslate"
                    value={field.state.value || ""}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isNaN(e.target.valueAsNumber)
                          ? 0
                          : e.target.valueAsNumber
                      )
                    }
                  />
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={
          isEdit
            ? t("payroll.lines.edit.previewTitle")
            : t("payroll.lines.add.previewTitle")
        }
        description={t("payroll.lines.form.previewDescription")}
        t={tPayroll}
        pending={pending}
        onConfirm={() => {
          if (!preview) return;
          if (line) {
            updateLine.mutate(
              { lineId: line.id, body: preview },
              {
                onSuccess: () => {
                  notifSilence(t("payroll.lines.feedback.updated"));
                  setPreview(null);
                  onOpenChange(false);
                },
                onError: (error) =>
                  notifSilenceError(apiErrorHandler(error) || error.message),
              }
            );
          } else {
            addLine.mutate(preview, {
              onSuccess: () => {
                notifSilence(t("payroll.lines.feedback.added"));
                setPreview(null);
                onOpenChange(false);
              },
              onError: (error) =>
                notifSilenceError(apiErrorHandler(error) || error.message),
            });
          }
        }}
      />
    </>
  );
}
